import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Paper,
  useTheme,
  Stack
} from '@mui/material';
import {
  Target,
  Lightbulb,
  Zap,
  Users,
  BarChart3,
  Shield,
  ArrowRight,
  Sparkles
} from 'lucide-react';

export default function AboutPage() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #fef2f2, #fdf2f8)',
        py: 8
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            icon={<Sparkles className="w-4 h-4" />}
            label="About Bean"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontSize: '1rem',
              py: 2,
              px: 2,
              mb: 3
            }}
          />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Transforming Pinterest Marketing
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
            We're on a mission to democratize Pinterest marketing through AI-powered automation
            and intelligent content management.
          </Typography>
        </Box>

        {/* Mission & Vision Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', bgcolor: 'white', borderRadius: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Target className="w-6 h-6" style={{ color: theme.palette.primary.main }} />
                  <Typography variant="h5" component="h2" sx={{ ml: 2, fontWeight: 600 }}>
                    Our Mission
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                  To empower creators and businesses with AI-driven tools that make Pinterest marketing
                  accessible, efficient, and effective for everyone.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', bgcolor: 'white', borderRadius: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Lightbulb className="w-6 h-6" style={{ color: theme.palette.primary.main }} />
                  <Typography variant="h5" component="h2" sx={{ ml: 2, fontWeight: 600 }}>
                    Our Vision
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                  To become the leading platform for Pinterest automation, helping businesses of all sizes
                  achieve their marketing goals through intelligent content creation and optimization.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* What Makes Us Different Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 4, fontWeight: 700 }}>
            What Makes Bean Different
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                icon: <Zap className="w-6 h-6" style={{ color: '#dc2626' }} />,
                title: "AI-Powered Content",
                description: "Advanced AI algorithms that understand your brand and create engaging content",
                bgColor: '#fee2e2'
              },
              {
                icon: <Users className="w-6 h-6" style={{ color: '#2563eb' }} />,
                title: "User-Centric Design",
                description: "Intuitive interface designed for both beginners and experienced marketers",
                bgColor: '#dbeafe'
              },
              {
                icon: <BarChart3 className="w-6 h-6" style={{ color: '#16a34a' }} />,
                title: "Data-Driven Insights",
                description: "Comprehensive analytics to optimize your Pinterest strategy",
                bgColor: '#dcfce7'
              },
              {
                icon: <Shield className="w-6 h-6" style={{ color: '#7c3aed' }} />,
                title: "Pinterest Compliance",
                description: "Built-in compliance checks to ensure your content meets Pinterest guidelines",
                bgColor: '#f3e8ff'
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={6} key={index} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: 'white',
                    borderRadius: 4,
                    boxShadow: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: feature.bgColor,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Stats Section */}
        <Paper
          elevation={0}
          sx={{
            p: 6,
            mb: 8,
            background: 'linear-gradient(to right, #dc2626, #db2777)',
            color: 'white',
            borderRadius: 4
          }}
        >
          <Grid container spacing={4} sx={{ textAlign: 'center' }}>
            {[
              { number: '10K+', label: 'Active Users' },
              { number: '1M+', label: 'Pins Created' },
              { number: '95%', label: 'Customer Satisfaction' },
              { number: '24/7', label: 'Support Available' }
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                  {stat.number}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fecaca' }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Ready to Transform Your Pinterest Strategy?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              size="large"
              sx={{
                bgcolor: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark
                },
                px: 4,
                py: 1.5
              }}
              endIcon={<ArrowRight className="w-4 h-4" />}
            >
              Get Started Free
            </Button>
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Contact Us
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
